import { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import SectionDetails from '../../_Sections/SectionDetails';
import SectionGallery from '../../_Sections/SectionGallery';
import SectionDropship from '../../_Sections/SectionDropship';
import SectionPairings from '../../_Sections/SectionPairings';
import SectionBranding from '../../_Sections/SectionBranding';
import SectionSizes from '../../_Sections/SectionSizes';
import { useSelector } from 'react-redux';
import WebsiteStore from '../../../WebsiteStore';
import { Box, Container, Grid, Stack, Typography, styled } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import SectionPricing from '../../_Sections/SectionPricing';
import tKey from '../../../Common/Utils/tKey';
import { useAppSelector } from '../../../Common/_hooks/useAppSelector';
import ProductSEO from '../../_Sections/ProductSEO';
import { BlackCheckedIcon } from '../../../Common/_components/CheckBoxes';
import SizeDisplay from '../../../Common/Measurements/SizeDisplay';

const PRODUCT_ID = 13;
const VIEW_PRODUCT_ID = 162;

export default function CottonSilkBandanasPage() {
  const [t] = useTranslation('page/products/cotton-silk-bandanas');
  const productData = useAppSelector(state => state.get('appData').get('products').get(String(PRODUCT_ID)));
  const locale = useAppSelector(state => state.get('userData').get('prefs').get('lang'))

  if (!productData) return null

  return <div className="centered-page product-page-v4">
    <ProductSEO
      viewProductId={VIEW_PRODUCT_ID}
      title={t('Custom all-over print Cotton Silk Bandanas | Products')}
      description={t('Design and create your own custom printed Cotton Silk Bandanas featuring your artwork. ✔ Print-on-demand drop shipping ✔ No minimums ✔ Made in Canada')}
    />
    <SectionDetails
      viewProductId={VIEW_PRODUCT_ID}
      color="#b5fff8"
      bgStyle={{
        backgroundPosition: 'bottom right',
        backgroundRepeat: 'no-repeat',
        backgroundSize: '40%',
      }}
      tag={t('The all-over print')}
      title={t('Cotton Silk Bandana')}
      text={t('Refine your wardrobe or your product line with cotton/silk scarves and bandanas! Made from lightweight airy cotton/silk voile, these scarves are both gorgeous and amazingly versatile. Wear them for any occasion. Each custom printed scarf or bandana is thoughtfully finished for a polished, professional look and printed with vibrant and permanent reactive inks.')}
      createUrl={productData.get('url_slug')}
      img={require('@resources/img/pages/products/cotton-silk-bandanas/banner-1-' + locale + '.png')}
      alt={t('Picture of Cotton Silk Bandana')}
      details={[
        {
          img: require('@resources/img/pages/products/cotton-silk-bandanas/icon-1.svg?url'),
          alt: t('Cotton Silk Fabric icon'),
          text: t('Made from airy cotton/silk voile fabric'),
        },
        {
          img: require('@resources/img/pages/products/cotton-silk-bandanas/icon-2.svg?url'),
          alt: t('Sewing machine icon'),
          text: t('Professional machine rolled hem finishing'),
        },
        {
          img: require('@resources/img/pages/products/cotton-silk-bandanas/icon-3.svg?url'),
        alt: t('Sizes icon'),
          text: t('Available in 7 sizes'),
        },
        {
          img: require('@resources/img/pages/products/cotton-silk-bandanas/icon-4.svg?url'),
          alt: t('Durable ink icon'),
          text: t('Durable and vibrant reactive print won’t fade with use or wash'),
        },
        {
          img: require('@resources/img/pages/products/cotton-silk-bandanas/icon-5.svg?url'),
          alt: t('Canada icon'),
          text: t('Digitally printed on demand in Montreal, Canada'),
        },
      ]}
    />
    <SectionGallery images={[
      require('@resources/img/pages/products/cotton-silk-bandanas/gallery_0.jpg'),
      require('@resources/img/pages/products/cotton-silk-bandanas/gallery_1.jpg'),
      require('@resources/img/pages/products/cotton-silk-bandanas/gallery_2.jpg'),
      require('@resources/img/pages/products/cotton-silk-bandanas/gallery_3.jpg'),
      require('@resources/img/pages/products/cotton-silk-bandanas/gallery_4.jpg'),
    ]} />

    <SectionDropship
      title={<Trans
        t={t}
        i18nKey={tKey('<yellow>Drop ship</yellow> your Cotton Silk Bandana<br/> <pink>on-demand</pink>')}
        components={{
          yellow: <span className="ty" />,
          pink: <span className="tp" />,
        }}
      />}
      text={t('Your brand and following will love Cotton Silk Bandana! At prices designed for reselling, Cotton Silk Bandana made on demand meet the needs of sellers who prefer not to stock inventory. Sell them in your eCommerce store and decide your profit margin')}
    />

    <SectionPricing
      productId={PRODUCT_ID}
      listOptions
      variants={['16x72','50x50']}
      options={['cotton-silk','10x45','16x72','16x16','21x21','26x26','36x36','50x50']}
    />

    <SectionSizes
      productId={PRODUCT_ID}
      title={t('Cotton Silk Bandana Sizing')}
      text={<Trans
        t={t}
        i18nKey={tKey('Available in 7 sizes including: <sizeSmall/> (Classic bandana size!), <sizeClassic/> (Classic bandana size!), <sizeMedium/>, <sizeLarge/>, <sizeExtraLarge/>, <sizeSkinny/> and <sizeLong/>')}
        components={{
          sizeSmall: <SizeDisplay sizes={[16, 16]} sourceUnit="in" displayType="physical" />,
          sizeClassic: <SizeDisplay sizes={[21, 21]} sourceUnit="in" displayType="physical" />,
          sizeMedium: <SizeDisplay sizes={[26, 26]} sourceUnit="in" displayType="physical" />,
          sizeLarge: <SizeDisplay sizes={[36, 36]} sourceUnit="in" displayType="physical" />,
          sizeExtraLarge: <SizeDisplay sizes={[50, 50]} sourceUnit="in" displayType="physical" />,
          sizeSkinny: <SizeDisplay sizes={[45, 10]} sourceUnit="in" displayType="physical" />,
          sizeLong: <SizeDisplay sizes={[72, 16]} sourceUnit="in" displayType="physical" />,
        }}
      />}
    >
      <img
        src={require('@resources/img/pages/products/cotton-silk-bandanas/measurement.svg?url')}
        style={{
          marginRight: 0,
        }}
      />
    </SectionSizes>

    <SectionBranding
      title={t('Brand your Cotton Silk Bandana')}
      text={t('Branding enhances your products. Add custom labels, artist cards or stickers to your packages to create a cohesive message and build your brand.')}
    />

    <SectionPairings
      text={t('Pair your Cotton Silk Bandana with these great products to create your own unique styles.')}
      productSlugs={['vegan-leather-backpack', 'vegan-leather-crossbody-purse', 'makeup-bags']}
    />
  </div>
}
